/**
 * Debug utility to log mapping between GeoJSON data and database records
 * for specific lot IDs without parsing the entire file directly.
 */

import { supabase } from './lib/supabase';

// Target lot IDs to track
const TARGET_LOTS = [
  'Angur-306',
  'Angur-308', 
  'Angur-309',
  'Angur-311',
  'Angur-312',
  'Angur-313',
  // Also try without the "Angur-" prefix to see if that's the issue
  '306',
  '308',
  '309'
];

interface LotMappingData {
  geoJsonId: string;
  geoJsonHolder: string;
  databaseId: string;
  databaseAvail: string;
  matchStatus: string;
}

export async function generateLotMappingReport(): Promise<LotMappingData[]> {
  try {
    console.log('Fetching lot data from database...');
    
    // Fetch all lot data from the database
    const { data: lotData, error: lotError } = await supabase
      .from('land_lots')
      .select('lot_id, lot_avail');
    
    if (lotError) {
      console.error('Error fetching lot data:', lotError);
      return [];
    }
    
    console.log(`Found ${lotData.length} lots in database`);
    
    // Create a lookup map for database records
    const dbLookup: Record<string, { lot_id: string, lot_avail: string }> = {};
    
        // Add entries for exact matches
        lotData.forEach(lot => {
          if (lot.lot_id) {
            const normalizedId = lot.lot_id.trim();
            dbLookup[normalizedId] = lot;
            
            // Also add lowercase version for case-insensitive matching
            dbLookup[normalizedId.toLowerCase()] = lot;
            
            // Try looking up without the "Angur-" prefix
            if (normalizedId.startsWith('Angur-')) {
              const numberOnly = normalizedId.substring(6); // Remove "Angur-"
              dbLookup[numberOnly] = lot;
            }
          }
        });
    
    console.log('Database lookup map created');
    
    // Find the target lots in the database
    const results: LotMappingData[] = [];
    
    TARGET_LOTS.forEach(targetId => {
      // Look for exact matches
      const exactMatch = dbLookup[targetId];
      const lowercaseMatch = dbLookup[targetId.toLowerCase()];
      
      results.push({
        geoJsonId: targetId,
        geoJsonHolder: 'Unknown (requires GeoJSON parsing)',
        databaseId: exactMatch ? exactMatch.lot_id : 
                    lowercaseMatch ? lowercaseMatch.lot_id : 'NOT FOUND',
        databaseAvail: exactMatch ? exactMatch.lot_avail : 
                        lowercaseMatch ? lowercaseMatch.lot_avail : 'UNKNOWN',
        matchStatus: exactMatch ? 'EXACT MATCH' : 
                     lowercaseMatch ? 'CASE-INSENSITIVE MATCH' : 'NO MATCH'
      });
    });
    
    // Output the results
    console.table(results);
    
    return results;
  } catch (error) {
    console.error('Error generating mapping report:', error);
    return [];
  }
}
